import { createSelector } from '@reduxjs/toolkit'
import store, { AppSelector } from 'app/store'
import getDataSelector, {
  getByIdSelector,
  getEntitiesByIdSelector,
} from 'core/utils/getDataSelector'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import moment from 'moment'
import { emptyObj } from 'utils/fp'
import { UNASSIGNED_CLUSTER } from '../infrastructure/constants'
import {
  cpuImagePath,
  memoryImagePath,
  storageImagePath,
} from '../orchestration/app-catalog/constant'
import { makeGetVMIdsByAppSelector } from '../orchestration/repositories/selector'
import { volumeSnapshotsSelector } from '../storage/snapshots/selectors'
import { leasePoliciesByTenantIdSelector, leasesByVmIdSelector } from './leases/selectors'
import { MigrationReducer } from './migrations/model'
import { latestMigrationByVmSelector, migrationsSelector } from './migrations/selectors'
import { ServerStates, IVirtualMachinesSelector, StatewiseVirtualMachineCount } from './newModel'
import {
  allServerGroupsByInstanceIdSelector,
  serverGroupsByInstanceIdSelector,
} from './server-groups/selectors'

export const VOLUME_BACKED_VM_STRING = 'None (Bootable Volume Used)'

// Can't import this from volume selectors due to circular dependency
const rawVolumesByIdSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumesLite>(DataKeys.OpenstackVolumesLite),
  (volumes) => {
    return volumes.reduce((accum, volume) => {
      return {
        ...accum,
        [volume.id]: { volume },
      }
    }, {})
  },
)

const snapshotsByVolumeIdSelector = createSharedSelector(
  volumeSnapshotsSelector,
  (volumeSnapshots) => {
    return volumeSnapshots.reduce((accum, snapshot) => {
      if (accum[snapshot?.volume_id]) {
        return {
          ...accum,
          [snapshot?.volume_id]: [...accum[snapshot.volume_id], snapshot],
        }
      }
      return {
        ...accum,
        [snapshot?.volume_id]: [snapshot],
      }
    }, {})
  },
)

const volumesByIdWithSnapshotsSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVolumes>(DataKeys.AllOpenstackVolumes),
  snapshotsByVolumeIdSelector,
  (volumes, volumeSnapshotsByVolumeId) => {
    return volumes.reduce((accum, volume) => {
      return {
        ...accum,
        [volume.id]: {
          ...volume,
          snapshots: volumeSnapshotsByVolumeId[volume?.id] || [],
        },
      }
    }, {})
  },
)
// End of volume selectors

/**
 * Returns VMs entities object for direct ID access
 * Use this when you need to access multiple VMs by ID without transformations
 * @example
 * const vmsById = useAppSelector(virtualMachinesEntitiesById)
 * const vm = vmsById[vmId] // Direct O(1) access
 */
export const virtualMachinesEntitiesById = getEntitiesByIdSelector(
  DataKeys.OpenstackVirtualMachines,
)

/**
 * Returns all VMs entities object for direct ID access
 */
export const allVirtualMachinesEntitiesById = getEntitiesByIdSelector(
  DataKeys.AllOpenstackVirtualMachines,
)

/**
 * Returns single VM entities object for direct ID access
 */
export const singleVirtualMachineEntitiesById = getEntitiesByIdSelector(
  DataKeys.SingleOpenstackVirtualMachine,
)

/**
 * Returns self VMs entities object for direct ID access
 */
export const selfVirtualMachinesEntitiesById = getEntitiesByIdSelector(
  DataKeys.SelfOpenstackVirtualMachines,
)

export const virtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVirtualMachines>(DataKeys.OpenstackVirtualMachines),
  serverGroupsByInstanceIdSelector,
  migrationsSelector,
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    serverGroupsByInstanceId,
    migrations: MigrationReducer,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    return vms.map((vm) => {
      // Volumes
      const volumeIds = vm?.volumeIds || []
      const volumeDetails = vm?.volumeDetails || {}
      const volumeNames = vm?.volumeNames || []

      // Migrations
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // ClusterName
      const clusterName =
        vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || UNASSIGNED_CLUSTER

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null

      // Image
      const imageId = vm?.imageDetails?.id
      const imageName =
        vm?.imageName || imageId || (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown')

      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: vm?.imageDetails,
        imageName,
        ownerName: vm?.ownerName,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: vm?.tenantName,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || '',
        flavorDisk: vm?.flavor?.disk || 0,
        flavorRam: vm?.flavor?.ram || 0,
        flavorVcpus: vm?.flavor?.vcpus || 0,
      }
    })
  },
)

export const allVirtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVirtualMachines>(DataKeys.AllOpenstackVirtualMachines),
  migrationsSelector,
  latestMigrationByVmSelector,
  allServerGroupsByInstanceIdSelector,
  (vms, migrations: MigrationReducer, latestMigrationByVmId, allServerGroupsByInstanceId) => {
    return vms.map((vm) => {
      // Volumes
      const volumeIds = vm?.volumeIds || []
      const volumeDetails = vm?.volumeDetails || {}
      const volumeNames = vm?.volumeNames || []

      // Migrations
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Cluster Name
      const clusterName =
        vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || UNASSIGNED_CLUSTER

      // Image
      const imageId = vm?.imageDetails?.id
      const imageName =
        vm?.imageName || imageId || (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown')

      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: vm?.imageDetails,
        imageName,
        ownerName: vm?.ownerName,
        volumeDetails,
        volumeNames,
        volumeIds,
        tenantName: vm?.tenantName,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || '',
        serverGroup: allServerGroupsByInstanceId?.[vm.id],
        flavorDisk: vm?.flavor?.disk || 0,
        flavorRam: vm?.flavor?.ram || 0,
        flavorVcpus: vm?.flavor?.vcpus || 0,
      }
    })
  },
)

export const singleVirtualMachineSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.SingleOpenstackVirtualMachine>(DataKeys.SingleOpenstackVirtualMachine),
  serverGroupsByInstanceIdSelector,
  migrationsSelector,
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    serverGroupsByInstanceId,
    migrations: MigrationReducer,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    return vms.map((vm) => {
      // Volumes
      const volumeIds = vm?.volumeIds || []
      const volumeDetails = vm?.volumeDetails || {}
      const volumeNames = vm?.volumeNames || []

      // Migrations
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Cluster Name
      const clusterName =
        vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || UNASSIGNED_CLUSTER

      // Image
      const imageId = vm?.imageDetails?.id
      const imageName =
        vm?.imageName || imageId || (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown')

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null

      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: vm?.imageDetails,
        imageName,
        ownerName: vm?.ownerName,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: vm?.tenantName,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || 'default',
      }
    })
  },
)

export const selfVirtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackVirtualMachines>(DataKeys.SelfOpenstackVirtualMachines),
  serverGroupsByInstanceIdSelector,
  migrationsSelector,
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    serverGroupsByInstanceId,
    migrations: MigrationReducer,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    return vms.map((vm) => {
      // Volumes
      const volumeIds = vm?.volumeIds || []
      const volumeDetails = vm?.volumeDetails ?? {}
      const volumeNames = vm?.volumeNames || []

      // Migrations
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Cluster Name
      const clusterName =
        vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone'] || UNASSIGNED_CLUSTER

      // Image
      const imageId = vm?.imageDetails?.id
      const imageName =
        vm?.imageName || imageId || (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown')

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null

      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: vm?.imageDetails,
        imageName,
        ownerName: vm?.ownerName,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: vm?.tenantName,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || 'default',
      }
    })
  },
)

export const virtualMachinesByIdSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    return {
      ...accum,
      [vm.id]: vm,
    }
  }, emptyObj),
)

export const virtualMachinesByHostSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    const hostId = vm?.['OS-EXT-SRV-ATTR:host']
    return {
      ...accum,
      [hostId]: accum[hostId] ? [...accum[hostId], vm] : [vm],
    }
  }, emptyObj),
)

export const virtualMachinesByIpSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    const vmAddresses =
      Object.entries(vm?.addresses || {})?.reduce((accum, [name, addresses]) => {
        const addressesIps = addresses?.reduce((accum, address) => {
          return {
            ...accum,
            [address?.addr]: vm,
          }
        }, {})
        return {
          ...accum,
          ...addressesIps,
        }
      }, {}) || {}
    return {
      ...accum,
      ...vmAddresses,
    }
  }, emptyObj),
)

export const computeQuotasSelector = createSharedSelector(virtualMachinesSelector, (vms) => {
  return vms?.reduce((accum, vm) => {
    return {
      ...accum,
      [vm?.tenant_id]: {
        ...accum[vm?.tenant_id],
        cores: (accum[vm?.tenant_id]?.cores || 0) + (vm?.flavor?.vcpus || 0),
        ram: (accum[vm?.tenant_id]?.ram || 0) + (vm?.flavor?.ram || 0),
      },
    }
  }, {})
})

export const makeVmCountsSelector = (
  defaultParams: { allTenants?: boolean } = { allTenants: false },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSharedSelector(
    virtualMachinesSelector,
    allVirtualMachinesSelector,
    selectParams,
    (vms, allVms, params) => {
      const vmScope = params?.allTenants ? allVms : vms
      return vmScope?.reduce(
        (accum, vm) => {
          if (['active', 'building'].includes(vm?.state)) {
            accum.vmCounts.success += 1
          } else if (['paused', 'suspended', 'stopped'].includes(vm?.state)) {
            accum.vmCounts.warning += 1
          } else {
            accum.vmCounts.error += 1
          }

          //it should first check the metadata values for hotplug as it contains the real time info whenever the VM gets hotplugged
          const hotplugMemory = Number(vm?.metadata?.['HOTPLUG_MEMORY'])
          const hotplugVcpus = Number(vm?.metadata?.['HOTPLUG_CPU'])

          accum.computeQuotas[vm?.tenant_id] = {
            ...accum.computeQuotas[vm?.tenant_id],
            cores:
              (accum.computeQuotas[vm?.tenant_id]?.cores || 0) +
              (hotplugVcpus || vm?.flavor?.vcpus || 0),
            ram:
              (accum.computeQuotas[vm?.tenant_id]?.ram || 0) +
              (hotplugMemory || vm?.flavor?.ram || 0),
          }

          return accum
        },
        {
          vmCounts: { success: 0, warning: 0, error: 0 },
          computeQuotas: {},
        },
      )
    },
  )
}

export const makeGetvirtualMachinesByAppSelector = (
  defaultParams: { appId?: string } = {
    appId: '',
  },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  const vmUidsByAppSelector = makeGetVMIdsByAppSelector()
  return createSelector(virtualMachinesSelector, selectParams, (vms, params) => {
    const vmUids = vmUidsByAppSelector(store.getState(), { appId: params?.appId || '' })
    // Filter VMs based on the retrieved VM IDs
    const filteredVms = vms?.filter((vm) => vmUids.includes(vm?.id))
    // Return the filtered VMs in the expected format
    return filteredVms?.map((vm) => ({
      name: vm?.name || `VM ${vm?.id}`,
      id: vm?.id,
      status: vm?.state || 'Unknown',
      taskState: vm?.taskState || 'Unknown',
      addresses: Object.entries(vm?.addresses || {}).reduce((accum, [networkName, addresses]) => {
        const addressList = addresses.map((address) => address.addr).filter(Boolean)
        return addressList.length ? { ...accum, [networkName]: addressList } : accum
      }, {}),
      flavor: [
        {
          label: 'CPUs',
          value: vm?.flavor?.vcpus || '-',
          imagePath: cpuImagePath,
          showDivider: true,
        },
        {
          label: 'Storage',
          value: vm?.flavor?.disk || '-',
          imagePath: storageImagePath,
          showDivider: true,
        },
        {
          label: 'Memory',
          value: vm?.flavor?.ram || '-',
          imagePath: memoryImagePath,
          showDivider: false,
        },
      ],
    }))
  })
}

// Lite VM List Selectors
export const virtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVirtualMachinesLite>(DataKeys.OpenstackVirtualMachinesLite),
  (vms) => vms,
)

export const virtualMachinesLiteByIdSelector = createSharedSelector(
  virtualMachinesLiteSelector,
  (vms) =>
    vms.reduce((accum, vm) => {
      return {
        ...accum,
        [vm.id]: vm,
      }
    }, emptyObj),
)

export const allVirtualMachinesLiteSelectorById = getByIdSelector(
  DataKeys.AllOpenstackVirtualMachinesLite,
)

export const allVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVirtualMachinesLite>(
    DataKeys.AllOpenstackVirtualMachinesLite,
  ),
  (vms) => vms,
)

export const allVirtualMachinesLiteByIdSelector = createSharedSelector(
  allVirtualMachinesLiteSelector,
  (vms) =>
    vms.reduce((accum, vm) => {
      return {
        ...accum,
        [vm.id]: vm,
      }
    }, emptyObj),
)

export const selfVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackVirtualMachinesLite>(
    DataKeys.SelfOpenstackVirtualMachinesLite,
  ),
  (vms) => vms,
)

export const selfStatewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackStatewiseVirtualMachinesLite>(
    DataKeys.SelfOpenstackStatewiseVirtualMachinesLite,
  ),
  (statewiseVms): StatewiseVirtualMachineCount => {
    const statewiseVMsData = statewiseVms[0]
    const success =
      statewiseVMsData?.[ServerStates.ACTIVE] + statewiseVMsData?.[ServerStates.BUILDING] || 0
    const warning =
      statewiseVMsData?.[ServerStates.STOPPED] + statewiseVMsData?.[ServerStates.PAUSED] || 0
    const error =
      statewiseVMsData?.[ServerStates.TOTAL] > 0
        ? statewiseVMsData?.[ServerStates.TOTAL] - success - warning || 0
        : 0
    return { success, warning, error, total: statewiseVMsData?.[ServerStates.TOTAL] || 0 }
  },
)

export const statewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
    DataKeys.OpenstackStatewiseVirtualMachinesLite,
  ),
  (statewiseVms): StatewiseVirtualMachineCount => {
    const statewiseVMsData = statewiseVms[0]
    const success =
      statewiseVMsData?.[ServerStates.ACTIVE] + statewiseVMsData?.[ServerStates.BUILDING] || 0
    const warning =
      statewiseVMsData?.[ServerStates.STOPPED] + statewiseVMsData?.[ServerStates.PAUSED] || 0
    const error =
      statewiseVMsData?.[ServerStates.TOTAL] > 0
        ? statewiseVMsData?.[ServerStates.TOTAL] - success - warning || 0
        : 0

    return {
      success,
      warning,
      error,
      total: statewiseVMsData?.[ServerStates.TOTAL] || 0,
    }
  },
)

export const allStatewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackStatewiseVirtualMachinesLite>(
    DataKeys.AllOpenstackStatewiseVirtualMachinesLite,
  ),
  (statewiseVms): StatewiseVirtualMachineCount => {
    const statewiseVMsData = statewiseVms[0]
    const success =
      statewiseVMsData?.[ServerStates.ACTIVE] + statewiseVMsData?.[ServerStates.BUILDING] || 0
    const warning =
      statewiseVMsData?.[ServerStates.STOPPED] + statewiseVMsData?.[ServerStates.PAUSED] || 0
    const error =
      statewiseVMsData?.[ServerStates.TOTAL] > 0
        ? statewiseVMsData?.[ServerStates.TOTAL] - success - warning || 0
        : 0
    return {
      success,
      warning,
      error,
      total: statewiseVMsData?.[ServerStates.TOTAL] || 0,
    }
  },
)

// Selector for Attach Volume - Send only active, stopped and suspended VMs
// export const vmListSelectorForAttachVolume = createSharedSelector(
//   getDataSelector<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
//     DataKeys.OpenstackStatewiseVirtualMachinesLite,
//   ),
//   (statewiseVms) => {
//     const activeVMs =
//       statewiseVms
//         ?.find((vm) => vm?.state === 'active')
//         ?.servers?.map((vm) => ({
//           ...vm,
//           state: 'active',
//         })) || []
//     const stoppedVMs =
//       statewiseVms
//         ?.find((vm) => vm?.state === 'stopped')
//         ?.servers?.map((vm) => ({
//           ...vm,
//           state: 'stopped',
//         })) || []
//     const suspendedVMs =
//       statewiseVms
//         ?.find((vm) => vm?.state === 'suspended')
//         ?.servers?.map((vm) => ({
//           ...vm,
//           state: 'suspended',
//         })) || []

//     return [...activeVMs, ...stoppedVMs, ...suspendedVMs] as ServerLite[]
//   },
// )
